import Button from '@/components/elements/forms/buttons/Button/Button';
import Icon from '@/components/icons/Icon';
import BottomSheetModal from '@/components/modules/modals/BottomSheetModal';
import { _t } from '@/locale';
import { useEffect, useRef, useState } from 'react';
import SelectAddOns from '../views/SelectAddOns';
import SelectEmployeeList from '../views/SelectEmployeeList';
import { canOfferEmployeeChange } from '../views/SelectEmployeeList.helpers';
import SelectService from '../views/SelectService';
import { EmployeeInfoContent } from './EmployeeInfoModal';
import SelectEmployeeCard from './SelectEmployeeCard';
import SelectionSummary from './SelectionSummary';
import LegacyServiceInfoCard from './LegacyServiceInfoCard';
import type { SelectionDrawerProps, SelectionDrawerView } from './SelectionDrawer.types';

const baseTranslationKey = 'features.bookingSelection.SelectionDrawer' as const;

const VIEW_TITLE_KEYS = {
  summary: `${baseTranslationKey}.title`,
  recommended: 'recommendedServices',
  addService: `${baseTranslationKey}.addService.title`,
  employee: 'features.bookingSelection.SelectEmployeeList.title',
  serviceInfo: 'features.bookingSelection.ServiceItem.infoTitle',
  // `employeeInfo` is titled with the employee's own name, so it has no key here.
} as const satisfies Record<Exclude<SelectionDrawerView, 'employeeInfo'>, string>;

/**
 * The mobile drawer over /tillval: a local view stack (not URL state) that the back arrow
 * pops. Presentational — selection changes go through the caller's handlers.
 */
const SelectionDrawer = ({
  isOpen,
  onClose,
  employee,
  summary,
  recommended,
  addService,
  onOpenAddService,
  onCancelAddService,
  onPickAnyEmployee,
  employeeList,
}: SelectionDrawerProps) => {
  const [stack, setStack] = useState<SelectionDrawerView[]>(['summary']);
  const view = stack[stack.length - 1];
  const [infoServiceId, setInfoServiceId] = useState<number | undefined>(undefined);
  // The list the (i) was tapped in: its `onHideServiceInfo` is the one to call when the
  // overlay goes, and by then the stack may no longer say which list that was.
  const infoSource = useRef<'recommended' | 'addService'>();
  const [infoEmployeeId, setInfoEmployeeId] = useState<number | undefined>(undefined);

  // Reset during render, not in an effect: the tracking effects below read the stack in the
  // same pass an effect-reset would land in, so a reopen over a list view would report a
  // phantom open + abandon before the reset caught up. Closing keeps the view for the sheet's
  // exit animation.
  const [wasOpen, setWasOpen] = useState(isOpen);
  if (isOpen !== wasOpen) {
    setWasOpen(isOpen);
    if (isOpen) setStack(['summary']);
  }

  const push = (next: SelectionDrawerView) => setStack((current) => [...current, next]);
  const pop = () => setStack((current) => (current.length > 1 ? current.slice(0, -1) : current));

  // With one bookable employee the view behind this card offers only "Valfri utövare" and that
  // person, so the card reports the pick without pretending to lead somewhere — unless the
  // pick itself is missing from that list, which the helper treats as a route worth keeping.
  const canChangeEmployee = canOfferEmployeeChange(employeeList.employees, employeeList.selectedEmployeeId);

  // Both lists are one add-service funnel: a pick in either reports itself through its own
  // `onPickService`, so the flag keeps the pop from also reading as an abandon.
  const addServicePicked = useRef(false);
  const pickRecommended = (serviceId: number, isRecommended: boolean) => {
    addServicePicked.current = true;
    recommended.onPickService(serviceId, isRecommended);
    pop();
  };

  const pickAddService = (serviceId: number, isRecommended: boolean) => {
    addServicePicked.current = true;
    addService?.onPickService(serviceId, isRecommended);
    pop();
  };

  const openServiceInfo = (serviceId: number) => {
    infoSource.current = view === 'recommended' ? 'recommended' : 'addService';
    setInfoServiceId(serviceId);
    push('serviceInfo');
  };

  const openEmployeeInfo = (employeeId: number) => {
    setInfoEmployeeId(employeeId);
    push('employeeInfo');
  };

  // The info views are overlays, not swapped content: the covered list stays mounted so
  // its scroll offset and expanded categories survive backing out.
  const isServiceInfoView = view === 'serviceInfo';
  const isEmployeeInfoView = view === 'employeeInfo';
  const isInfoView = isServiceInfoView || isEmployeeInfoView;
  const baseView = isInfoView ? stack[stack.length - 2] : view;

  // Looked up fresh so the card tracks prop refreshes, and only in the list the (i)
  // was tapped in — the same id can sit in both, with a different CTA behind each.
  const infoService = !isServiceInfoView
    ? undefined
    : baseView === 'recommended'
    ? recommended.services.find((service) => service.id === infoServiceId)
    : addService?.categories.flatMap((category) => category.services).find((service) => service.id === infoServiceId);

  const infoEmployee = !isEmployeeInfoView
    ? undefined
    : employeeList.employees.find((employee) => employee.id === infoEmployeeId);

  // The row can vanish underneath the overlay (props refresh, e.g. hydration
  // filtering a just-picked add-on); drop the dead entry so the header falls back too.
  const hasInfoSubject = isServiceInfoView ? Boolean(infoService) : Boolean(infoEmployee);
  useEffect(() => {
    if (!isInfoView || hasInfoSubject) return;
    setStack((current) => (current.length > 1 ? current.slice(0, -1) : current));
  }, [isInfoView, hasInfoSubject]);

  const isInfoOpen = isInfoView && hasInfoSubject;

  // Every way the service overlay goes — back arrow, the drawer closing over it, its row
  // vanishing, its own CTA — is the list's `onHideServiceInfo` (ServiceItem's own drawer
  // reports the same). The CTA path pops before it picks, so the pick lands first and this
  // effect reports the hide after it, the order the modal arm uses.
  const isServiceInfoShown = isOpen && isServiceInfoView && hasInfoSubject;
  const wasServiceInfoShown = useRef(false);
  useEffect(() => {
    if (wasServiceInfoShown.current && !isServiceInfoShown && infoServiceId !== undefined) {
      const onHide =
        infoSource.current === 'recommended' ? recommended.onHideServiceInfo : addService?.onHideServiceInfo;
      onHide?.(infoServiceId);
    }
    wasServiceInfoShown.current = isServiceInfoShown;
  }, [isServiceInfoShown, infoServiceId, recommended.onHideServiceInfo, addService?.onHideServiceInfo]);

  // The add-service funnel's open and abandon legs, for either list view — recommended or
  // main. A pick pops the view after reporting itself, so it must not also read as an
  // abandon; any other exit — back arrow, drawer close — does.
  const isAddServiceViewOpen = isOpen && (stack.includes('addService') || stack.includes('recommended'));
  const wasAddServiceViewOpen = useRef(false);
  useEffect(() => {
    if (isAddServiceViewOpen && !wasAddServiceViewOpen.current) {
      addServicePicked.current = false;
      onOpenAddService?.();
    } else if (!isAddServiceViewOpen && wasAddServiceViewOpen.current && !addServicePicked.current) {
      onCancelAddService?.();
    }
    wasAddServiceViewOpen.current = isAddServiceViewOpen;
  }, [isAddServiceViewOpen, onOpenAddService, onCancelAddService]);

  // The drawer renders the CTA on the sheet below the card, so keep it out of the card's props.
  const { onSearchTimes, ...summaryCardProps } = summary;

  const content = (() => {
    switch (baseView) {
      case 'summary':
        return (
          <div className="gap-md flex flex-col">
            {employee && (
              <SelectEmployeeCard
                name={employee.name}
                jobTitle={employee.jobTitle}
                avatarUrl={employee.avatarUrl}
                avatarIcon={employee.avatarIcon}
                rating={employee.rating}
                rightSlot={
                  canChangeEmployee ? <Icon variant="chevron-right" color="fg_secondary" size="sm" /> : undefined
                }
                onClick={canChangeEmployee ? () => push('employee') : undefined}
              />
            )}
            <SelectionSummary
              {...summaryCardProps}
              onAddServices={addService && addService.categories.length > 0 ? () => push('addService') : undefined}
              onAddRecommended={recommended.services.length > 0 ? () => push('recommended') : undefined}
            />
            {onSearchTimes ? (
              <Button
                variant="primary"
                size="lg"
                block
                label={_t('features.bookingSelection.SelectionSummary.cta.searchTimes')}
                leftIcon={<Icon variant="search" size="xs" color="fg_on_primary" />}
                onClick={onSearchTimes}
              />
            ) : onPickAnyEmployee ? (
              <Button
                variant="primary"
                size="lg"
                block
                label={_t('features.bookingSelection.anyEmployee')}
                rightIcon={<Icon variant="arrow-right" size="xs" color="fg_on_primary" />}
                onClick={onPickAnyEmployee}
              />
            ) : null}
          </div>
        );
      case 'recommended':
        return (
          <SelectAddOns {...recommended} isNarrow onOpenServiceInfo={openServiceInfo} onPickService={pickRecommended} />
        );
      case 'addService':
        if (!addService) return null;
        return (
          <SelectService
            {...addService}
            isNarrow
            ctaAction="add"
            onOpenServiceInfo={openServiceInfo}
            onPickService={pickAddService}
          />
        );
      case 'employee': {
        const onPick = (employeeId: number) => {
          employeeList.onPick(employeeId);
          pop();
        };
        // Returns to the summary card that led here, not out of the drawer.
        return (
          <SelectEmployeeList
            {...employeeList}
            onPick={onPick}
            indicator="select"
            onOpenEmployeeInfo={openEmployeeInfo}
          />
        );
      }
      // Never a base — an overlay only sits on a list view; guarded for the type.
      case 'serviceInfo':
      case 'employeeInfo':
        return null;
      default: {
        const never: never = baseView;
        console.error('Invalid SelectionDrawer view', never);
        return null;
      }
    }
  })();

  const infoOverlay = (() => {
    if (!isInfoOpen) return null;

    if (infoEmployee) {
      return (
        // z-10: the list's drop-shadowed cards form stacking contexts that would paint above.
        <div
          data-cy="drawer-employee-info"
          className="bg-surface_mp px-md py-md relative z-10 col-start-1 row-start-1 overflow-y-auto">
          <EmployeeInfoContent employee={infoEmployee} />
        </div>
      );
    }

    if (!infoService) return null;

    // Pop the overlay first, then apply the same post-pick rule as the list underneath.
    const addInfoService = () => {
      pop();
      if (baseView === 'recommended') pickRecommended(infoService.id, infoService.isRecommended);
      else pickAddService(infoService.id, infoService.isRecommended);
    };

    return (
      // z-10: the list's drop-shadowed cards form stacking contexts that would paint above.
      <div
        data-cy="drawer-service-info"
        className="bg-surface_mp px-md py-md relative z-10 col-start-1 row-start-1 overflow-y-auto">
        <div className="gap-md flex flex-col">
          <LegacyServiceInfoCard service={infoService} />
          <Button block variant="primary" size="md" onClick={addInfoService}>
            {_t('features.bookingSelection.ServiceItem.infoCta.add')}
          </Button>
        </div>
      </div>
    );
  })();

  return (
    <BottomSheetModal
      isOpen={isOpen}
      onClose={onClose}
      // The overlay is titled with the person it presents; a dead entry falls back to the
      // list's own title for the frame before the stack pops.
      title={view === 'employeeInfo' ? infoEmployee?.name ?? _t(VIEW_TITLE_KEYS.employee) : _t(VIEW_TITLE_KEYS[view])}
      onBack={stack.length > 1 ? pop : undefined}
      backLabel={_t('back')}>
      {/* One grid cell, not absolute: both layers size the sheet, so a short list cannot clip the CTA. */}
      <div className="grid min-h-0 flex-1">
        {/* inert: no focus or clicks under the overlay. aria-hidden stays for role queries in tests
            (jsdom ignores inert) and pre-inert browsers. React 18 only forwards inert as a string. */}
        <div
          inert={isInfoOpen ? '' : undefined}
          aria-hidden={isInfoOpen || undefined}
          className="bg-surface_mp px-md py-md col-start-1 row-start-1 overflow-y-auto">
          {content}
        </div>
        {infoOverlay}
      </div>
    </BottomSheetModal>
  );
};

export default SelectionDrawer;
